import React, { useEffect, useState } from 'react';
import { UIProperty } from '@api/v1';
import { Autocomplete, Checkbox, Chip, TextField } from '@mui/material';
import { useSelector } from 'react-redux';
import { State } from '../reducers';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';
import { useTranslation } from 'react-i18next';
import { findBestPropertyMatch } from './fuzzy-property-match';

interface PropertySelectProps {
	customerId: number | null;
	value: UIProperty[];
	onChange: (properties: UIProperty[]) => void;
	disabled?: boolean;
	variant?: 'standard' | 'outlined' | 'filled';
	size?: 'small' | 'medium';
	validate?: boolean;
	description?: string;
}

export function PropertySelect({
	customerId,
	value,
	onChange,
	disabled,
	variant = 'standard',
	size = 'medium',
	validate = false,
	description
}: PropertySelectProps) {
	const { t } = useTranslation();
	const properties = useSelector((state: State) => state.properties);
	const [inputValue, setInputValue] = useState('');

	useEffect(() => {
		if (validate) {
			ValidatorForm.addValidationRule('hasPropertySelected', () => {
				return !!value?.length;
			});
		}
	}, [value, validate]);

	const filteredOptions =
		properties?.list?.data?.filter((property) => property.customer.id === customerId) || [];

	useEffect(() => {
		if (!description || value?.length || !filteredOptions.length) return;

		const match = findBestPropertyMatch(description, filteredOptions);
		if (match) {
			onChange([match]);
		}
	}, [description, customerId, filteredOptions.length]);

	return (
		<Autocomplete
			multiple
			fullWidth
			size={size}
			disabled={disabled || !customerId}
			options={filteredOptions}
			isOptionEqualToValue={(option, val) => option.id === val.id}
			getOptionLabel={(property) => property.name}
			onChange={(_event, newValue: UIProperty[]) => {
				onChange(newValue);
			}}
			onInputChange={(_event, val) => {
				setInputValue(val);
			}}
			value={value}
			inputValue={inputValue}
			renderInput={(params) =>
				validate ? (
					<TextValidator
						variant={variant}
						{...params}
						fullWidth
						value={inputValue}
						label={t('insurances:add_dialog_property')}
						validators={['hasPropertySelected']}
						errorMessages={[t('general:required_message')]}
					/>
				) : (
					<TextField variant={variant} {...params} fullWidth />
				)
			}
			renderOption={(props, option, { selected }) => (
				<li {...props}>
					<Checkbox style={{ marginRight: 8 }} checked={selected} />
					{option.name}
				</li>
			)}
			renderTags={(tagValue, getTagProps) =>
				tagValue.map((option, index) => (
					<Chip variant="outlined" label={option.name} size="small" {...getTagProps({ index })} />
				))
			}
		/>
	);
}
